import type { Task } from '../../types';
import type { BuildStep } from './constants';
import { BuildSteps } from './constants';

function taskHasNoMoreRetries(task: Task): boolean {
  if (!task.attempt_number || !task.maxRetries) {
    return true;
  }
  return task.attempt_number > task.maxRetries;
}

const getBuildStep = (task?: Task, isSteady?: boolean): BuildStep => {
  if (!task || task.status === 'CREATED') {
    return BuildSteps.Queued;
  }

  if (task.status === 'FAILED' && taskHasNoMoreRetries(task)) {
    return BuildSteps.Failed;
  }
  if (task.status === 'SUCCEEDED' && (isSteady ?? true)) {
    return BuildSteps.Succeeded;
  }
  if (task.status === 'SUCCEEDED' && !isSteady) {
    return BuildSteps.AnalyzingDeps;
  }

  return BuildSteps.ApplyingBuild;
};

// hasTaskStarted returns whether the task has gotten past the CREATED or ASSIGNED states.
// If task is falsy this function returns false.
const hasTaskStarted = (task?: Pick<Task, 'status'>): boolean =>
  !!task && task.status !== 'CREATED' && task.status !== 'ASSIGNED';

const codeSentryTasks = new Set(['CodeSentryDataSync', 'BinaryDecompositionBuild']);

// isTaskFinished returns whether the scan has finished (whether successfully or not).
// If task is falsy this function returns false.
const isTaskFinished = (task?: Task): boolean => {
  if (!task) {
    return false;
  }
  const isTaskFailed = task?.status === 'FAILED' && taskHasNoMoreRetries(task);
  // CodeSentry integration involves multiple tasks.  If the task is a CodeSentry task and it has not failed,
  // then the task is not finished and we should continue polling.
  if (codeSentryTasks.has(task.task) && !isTaskFailed) {
    return false;
  }

  return (
    task.status === 'SUCCEEDED' || isTaskFailed
  );
};

const taskFns = {
  getBuildStep,
  hasTaskStarted,
  isTaskFinished,
  taskHasNoMoreRetries,
};

export default taskFns;
